/**
 * Chart utility functions for interval calculations and formatting
 */
import moment from 'moment'

import { TimeInterval } from 'types/search/chart'

/**
 * Get interval duration in milliseconds
 */
export const getIntervalMinutes = (interval: TimeInterval): number => {
  switch (interval) {
    case TimeInterval.ONE_SECOND:
      return 1000
    case TimeInterval.TEN_SECONDS:
      return 10 * 1000
    case TimeInterval.ONE_MINUTE:
      return 60 * 1000
    case TimeInterval.TEN_MINUTES:
      return 10 * 60 * 1000
    case TimeInterval.ONE_HOUR:
      return 60 * 60 * 1000
    case TimeInterval.THREE_HOURS:
      return 3 * 60 * 60 * 1000
    case TimeInterval.ONE_DAY:
      return 24 * 60 * 60 * 1000
  }
}

/**
 * Get interval duration with appropriate unit
 */
export const getIntervalDuration = (
  interval: TimeInterval,
): { amount: number; unit: moment.unitOfTime.DurationConstructor } => {
  switch (interval) {
    case TimeInterval.ONE_SECOND:
      return { amount: 1, unit: 'seconds' }
    case TimeInterval.TEN_SECONDS:
      return { amount: 10, unit: 'seconds' }
    case TimeInterval.ONE_MINUTE:
      return { amount: 1, unit: 'minutes' }
    case TimeInterval.TEN_MINUTES:
      return { amount: 10, unit: 'minutes' }
    case TimeInterval.ONE_HOUR:
      return { amount: 1, unit: 'hours' }
    case TimeInterval.THREE_HOURS:
      return { amount: 3, unit: 'hours' }
    case TimeInterval.ONE_DAY:
      return { amount: 1, unit: 'days' }
  }
}

/**
 * Calculate the exact start and end dates for a bar interval
 */
export const calculateBarInterval = (
  clickedDate: Date,
  interval: TimeInterval,
): [Date, Date] => {
  const { amount, unit } = getIntervalDuration(interval)
  const clickedMoment = moment(clickedDate)

  // The bar represents an interval starting at clickedDate
  const startDate = clickedMoment.toDate()
  const endDate = clickedMoment.add(amount, unit).toDate()

  return [startDate, endDate]
}

/**
 * Format time interval for display using moment.js
 */
export function formatInterval(start: string, end: string): string {
  const mStart = moment(start)
  const mEnd = moment(end)

  if (!mStart.isValid() || !mEnd.isValid()) return ''

  // Calculate duration
  const diffSeconds = mEnd.diff(mStart, 'seconds')
  const diffMinutes = mEnd.diff(mStart, 'minutes')
  const diffHours = mEnd.diff(mStart, 'hours')
  const diffDays = mEnd.diff(mStart, 'days')

  // Determine duration label (rounded up)
  let durationLabel
  if (diffSeconds < 60) {
    const seconds = Math.ceil(diffSeconds)
    durationLabel = `${seconds} second${seconds !== 1 ? 's' : ''}`
  } else if (diffMinutes < 60) {
    const minutes = Math.ceil(diffMinutes)
    durationLabel = `${minutes} minute${minutes !== 1 ? 's' : ''}`
  } else if (diffHours < 24) {
    const hours = Math.ceil(diffHours)
    durationLabel = `${hours} hour${hours !== 1 ? 's' : ''}`
  } else {
    const days = Math.ceil(diffDays)
    durationLabel = `${days} day${days !== 1 ? 's' : ''}`
  }

  const showSeconds = diffSeconds < 60
  const timeFormat = showSeconds ? 'HH:mm:ss' : 'HH:mm'

  // Same day - no year
  if (mStart.isSame(mEnd, 'day')) {
    return (
      `${mStart.format(`MMM D, ${timeFormat}`)} – ` +
      `${mEnd.format(timeFormat)} (${durationLabel})`
    )
  }

  // Same year - no year
  if (mStart.isSame(mEnd, 'year')) {
    return (
      `${mStart.format(`MMM D, ${timeFormat}`)} – ` +
      `${mEnd.format(`MMM D, ${timeFormat}`)} (${durationLabel})`
    )
  }

  // Different years - show years
  return (
    `${mStart.format(`MMM D, YYYY, ${timeFormat}`)} – ` +
    `${mEnd.format(`MMM D, YYYY, ${timeFormat}`)} (${durationLabel})`
  )
}

/**
 * Format hover tooltip text based on bar interval duration
 */
export function formatHoverText(start: string, end: string): string {
  const mStart = moment(start)
  const mEnd = moment(end)

  if (!mStart.isValid() || !mEnd.isValid()) return ''

  const diffHours = mEnd.diff(mStart, 'hours')

  // For intervals < 1 day: show only time interval
  if (diffHours < 24) {
    const diffSeconds = mEnd.diff(mStart, 'seconds')
    const timeFormat = diffSeconds < 60 ? 'HH:mm:ss' : 'HH:mm'
    return `${mStart.format(timeFormat)} – ${mEnd.format(timeFormat)}`
  }

  // For intervals >= 1 day: show day format "Fri Nov 7 - Sat Nov 8"
  return `${mStart.format('ddd MMM D')} - ${mEnd.format('ddd MMM D')}`
}
